/* eslint-disable complexity */
import moment from 'moment';
import { v4 as uniqueId } from 'uuid';
import { getClientCode, getConfigField } from 'src/models/lmsConfig/queries';
import axios from 'src/workspace/axios';
import {
  ANALYTICS_EVENT_ID_STORAGE_KEY,
  ANALYTICS_SESSION_ID_STORAGE_KEY,
  ANALYTICS_SESSION_TIMEOUT,
} from './constants';
import type { AnalyticsConfig, AnalyticsTrackParams } from './types';

export type { AnalyticsConfig } from './types';

const getAnalyticsStorageIds = () => {
  const currentTime = Date.now();
  const storedEventID =
    Number(window.localStorage.getItem(ANALYTICS_EVENT_ID_STORAGE_KEY)) || 0;
  const storedSessionID = Number(
    window.localStorage.getItem(ANALYTICS_SESSION_ID_STORAGE_KEY),
  );

  const hasSessionTimedOut =
    currentTime - storedSessionID > ANALYTICS_SESSION_TIMEOUT;
  const eventID = hasSessionTimedOut ? storedEventID + 1 : storedEventID;
  const sessionID =
    Number.isInteger(storedSessionID) && !hasSessionTimedOut
      ? storedSessionID
      : currentTime;

  if (hasSessionTimedOut) {
    window.localStorage.setItem(
      ANALYTICS_EVENT_ID_STORAGE_KEY,
      String(eventID),
    );
    window.localStorage.setItem(
      ANALYTICS_SESSION_ID_STORAGE_KEY,
      String(sessionID),
    );
  }

  return { eventID, sessionID };
};

const buildInstrumentationRequestBody = ({
  clientCode,
  type,
  eventName,
  eventType = 'track',
  activity_type,
  module,
  pathname,
  eventID,
  sessionID,
  version,
  source,
  payload,
}: AnalyticsTrackParams) => {
  const screenWidth =
    window?.innerWidth ||
    document?.documentElement?.clientWidth ||
    document?.body?.clientWidth;
  const timestamp = Date.now();
  const originalTimestampFormatted = moment(timestamp).format(
    'YYYY-MM-DD HH:mm:ss',
  );
  const userId = getConfigField('vymo_id') || '';
  const properties = {
    version,
    route: pathname,
    module,
    activity_type,
    client: clientCode,
    payload,
    source,
    session_id: sessionID,
    event_id: eventID,
  };

  const event: any = {
    anonymousId: uniqueId(),
    channel: clientCode,
    context: {
      source: 'web',
      locale: navigator?.language || 'en',
      timezone: moment().format('Z'),
      screenWidth,
    },
    messageId: uniqueId(),
    originalTimestamp: originalTimestampFormatted,
    integrations: null,
    projectId: '604',
    sentAt: timestamp,
    timestamp,
    type: eventType,
    userId: userId || null,
    version: null,
  };

  if (eventType === 'identify') {
    event.traits = { userId };
  } else {
    event.event = eventName ?? type;
    event.properties = properties || {};
  }

  return {
    vymoSourceType: 'web',
    timestamp,
    events: [event],
  };
};

export const trackAnalyticsActions = (config: AnalyticsConfig) => {
  const {
    type,
    isPageView = false,
    source = null,
    module,
    payload = undefined,
  } = config;

  const pathname = window.location.hash;
  try {
    const eventName = payload?.event_name ?? type;
    const { eventID, sessionID } = getAnalyticsStorageIds();
    axios.post(
      '/vymo/v3/app-instrumentation/events',
      buildInstrumentationRequestBody({
        version: '1.0.0',
        clientCode: getClientCode(),
        activity_type: type,
        module,
        eventName,
        eventType: isPageView ? 'page' : 'track',
        type,
        pathname,
        eventID,
        sessionID,
        source: source || undefined,
        payload,
      }),
    );
  } catch (e) {
    // eslint-disable-next-line no-console
    console.log(e);
  }
};
